import * as React from "react";

import { Placeholder } from "kanopi-components/Placeholder";
import { useTrackIdentity } from "kanopi-components/tracking";

import { useAuthenticate } from "../hooks/useAuthenticate";
import { useIsLoggedIn } from "../hooks/useIsLoggedIn";
import { useUser } from "../useUser";
import { useNavigate } from "@reach/router";
import { useLogout } from "../hooks/useLogout";
import { useLocation } from "@reach/router";

type TAuthRequired = {
  lazy?: boolean;
  login?: boolean;
  admin?: boolean;
  anonymous?: boolean;
  hijacking?: boolean;
  children?: React.ReactNode;
};

export const AuthRequired: React.FC<TAuthRequired> = ({
  lazy = false,
  login = false,
  admin = false,
  anonymous = false,
  hijacking = false,
  children,
}) => {
  const user = useUser();
  const trackIdentity = useTrackIdentity({ now: false });
  React.useEffect(() => {
    if (user?.uuid) {
      trackIdentity({
        userId: user?.uuid,
        properties: {
          firstName: user?.firstName,
          lastName: user?.lastName,
          distributionPartner: user?.latestReferrer?.key,
          createdAt: user?.dateJoined,
        },
      });
    }
  }, [user?.uuid, user?.firstName, user?.lastName]);
  if (lazy === true) {
    return <LazyAuthRequired>{children}</LazyAuthRequired>;
  }
  if (login === true) {
    return <LoginAuthRequired>{children}</LoginAuthRequired>;
  }
  if (admin === true) {
    return <AdminAuthRequired>{children}</AdminAuthRequired>;
  }
  if (anonymous === true || hijacking === true) {
    return <>{children}</>;
  }
  return <></>;
};

const LazyAuthRequired = ({ children }: { children?: React.ReactNode }) => {
  const { loading } = useAuthenticate({ lazy: true, now: true });
  return (
    <Placeholder loading={loading} fullPage={true}>
      {children}
    </Placeholder>
  );
};

const LoginAuthRequired = ({
  children,
}: {
  children?: React.ReactNode;
  nextUrl?: string;
}) => {
  const isLoggedIn = useIsLoggedIn();
  const navigate = useNavigate();
  const user = useUser();
  const { logout } = useLogout();
  const location = useLocation();
  if (!isLoggedIn) {
    navigate(`/login?nextUrl=${location.pathname}`);
    return null;
  } else {
    if (user?.allowAdminPortalAccess) {
      // TODO: For some reason the below redirect won't work as `AuthRequired` seems to
      // run again with `login=true` after the redirect happens, which makes
      // `location.pathname` incorrect on the second run. Disabling for now due to little
      // benefit of `nextUrl` today.
      // navigate(`/login/?nextUrl=${location.pathname}`);
      //   navigate("/login/");
      // } else if (user.allowAdminPortalAccess) {
      React.useEffect(() => {
        logout();
      }, []);
      return null;
    }

    return <>{children}</>;
  }
};

const AdminAuthRequired = ({
  children,
}: {
  children?: React.ReactNode;
  nextUrl?: string;
}) => {
  const isLoggedIn = useIsLoggedIn();
  const navigate = useNavigate();
  const user = useUser();
  const { logout } = useLogout();
  if (!isLoggedIn) {
    navigate("/admin/login/");
    return null;
  } else {
    if (!user?.allowAdminPortalAccess) {
      // TODO: For some reason the below redirect won't work as `AuthRequired` seems to
      // run again with `login=true` after the redirect happens, which makes
      // `location.pathname` incorrect on the second run. Disabling for now due to little
      // benefit of `nextUrl` today.
      // navigate(`/login/?nextUrl=${location.pathname}`);
      // navigate("/admin/login/");
      React.useEffect(() => {
        logout();
      }, []);
      return null;
    }

    return <>{children}</>;
  }
};
